import React, { useRef, useEffect, useState, useContext } from 'react';
import './Exchange.css';
import '../pages/InvestmentsNew.css';
import { Link, useHistory } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import getExchange from  "../../MongoDb/GetExchange";
import { url as API_BASE_URL } from '../../MongoDb/MongoConstants';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

import {
    SportsFootball,
    SportsBaseball,
    SportsBasketball,
    SportsHockey,
    SportsGolf,
    Sports,
    SportsScore,
    FilterList,
    Search as SearchIcon,
    AccountBalance,
    TrendingUp,
    Receipt
} from '@mui/icons-material';
import '../shared/PageHeader.css';
import { is } from 'date-fns/locale';

const howPages = [

`<h2>How It Works</h2>
 <p>
   Each security represents a fully collateralized contract that pays
   <strong>$1,000</strong> if a specific event occurs.
 </p>
 <p>
   If the event does not occur, the security settles at <strong>$0</strong>.
 </p>
 <p>
   Example: "Tiger Woods to Win The Masters."
   If Tiger wins → the security pays $1,000.
   If not → it settles at $0.
 </p>
 <p>
   The payout is tied to the contract itself and remains attached to the security
   regardless of how many times it is traded.
 </p>`,

`<h3>Initial Offering (IPO Pricing)</h3>
 <p>
   Before open trading begins, securities are listed at an initial offering price
   derived from current market odds.
 </p>
 <p>
   IPO Price = Implied Probability × $1,000
 </p>
 <ul>
   <li>+100 (50%) → $500 IPO price</li>
   <li>+200 (33%) → $333 IPO price</li>
   <li>+400 (20%) → $200 IPO price</li>
   <li>-150 (60%) → $600 IPO price</li>
 </ul>
 <p>
   This ensures the starting price reflects the broader market's probability estimate.
 </p>`,

`<h3>Open Market Trading</h3>
 <p>
   After the IPO phase, securities trade freely between participants
   in an open marketplace.
 </p>
 <ul>
   <li>Buy if you believe the implied probability is too low</li>
   <li>Sell if you believe the implied probability is too high</li>
   <li>Exit positions at any time before settlement</li>
 </ul>
 <p>
   Prices are determined entirely by supply and demand.
   The platform matches buyers and sellers electronically.
 </p>`,

`<h3>How Pricing Reflects Probability</h3>
 <p>
   Because each security pays $1,000 upon success,
   the market price reflects the implied probability of the event occurring.
 </p>
 <ul>
   <li>$250 price ≈ 25% implied probability</li>
   <li>$600 price ≈ 60% implied probability</li>
   <li>$800 price ≈ 80% implied probability</li>
 </ul>
 <p>
   Prices update continuously as participants trade and new information becomes available.
 </p>`,

`<h3>Ownership & Profit</h3>
 <p><strong>If you buy at $400:</strong></p>
 <ul>
   <li>If the event occurs → settles at $1,000 (Profit: $600)</li>
   <li>If it does not occur → settles at $0 (Loss: $400)</li>
 </ul>

 <p><strong>If you sell at $700:</strong></p>
 <ul>
   <li>You receive $700 immediately</li>
   <li>You transfer ownership of the security</li>
   <li>You have no further exposure to the event outcome</li>
 </ul>
 <p>
   The $1,000 payout right remains attached to the contract itself,
   not to any previous holder.
 </p>`,

`<h3>Settlement</h3>
 <p>
   At the conclusion of the event, each security settles automatically
   to either <strong>$1,000</strong> or <strong>$0</strong>.
 </p>
 <p>
   Settlement is determined using the predefined official result source
   published for each contract.
 </p>
 <p>
   There is no margin, no borrowing, and no future payment obligation
   for prior holders.
 </p>`,

`<h3>Risk Disclosure</h3>
 <ul>
   <li>Securities may expire worthless</li>
   <li>Prices can be volatile</li>
   <li>Liquidity is not guaranteed</li>
   <li>Participants may lose their full investment</li>
 </ul>
 <p>
   Only participate with capital you are prepared to risk.
 </p>`

];


function Exchange() {
    const { user, userProfile, fetchUserProfile} = useContext(AuthContext);
    // const [profile, setProfile] = useState(null);
    const { isDarkMode } = useTheme();
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSport, setSelectedSport] = useState('ALL');
    const history = useHistory();
    const [searchQuery, setSearchQuery] = useState('');
    const [showHowModal, setShowHowModal] = useState(false);
    const [howPage, setHowPage] = useState(0);
    const [highlight, setHighlight] = useState(false);
    const [highlightedTeams, setHighlightedTeams] = useState({});
    const prevPriceRef = useRef();
    const prevPricesRef = useRef([]);
    // const [profile, setProfile] = useState(null);

    // Portfolio sidebar state
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    // Inline detail panel state
    const [selectedTeam, setSelectedTeam] = useState(null); // team_symbol string
    const [detailData, setDetailData] = useState(null);     // full team detail
    const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
    const [recentTrades, setRecentTrades] = useState([]);
    const [stats24hr, setStats24hr] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailPriceFlash, setDetailPriceFlash] = useState(false);
    const [prevDetailPrice, setPrevDetailPrice] = useState(null);

    // Scrolling ticker state
    const [tickerData, setTickerData] = useState([]);
    const [showStickyBar, setShowStickyBar] = useState(false);

    // Order form state
    const [orderType, setOrderType] = useState('buy');
    const [orderPrice, setOrderPrice] = useState('');
    const [orderQty, setOrderQty] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderMsg, setOrderMsg] = useState(null);
    const [orderAction, setOrderAction] = useState('market');

    const fetchexchangedata = async () => {
        try {
            const exchange = await getExchange();
            exchange.sort((a, b) => b.current_price - a.current_price);

            const enrichedExchange = exchange.map(team => {
                let priceChangePercent = 0;
                if (team.price_history && team.price_history.length >= 2) {
                    const currentPrice = team.current_price;
                    const oldPrice = team.price_history[0].price;
                    priceChangePercent = ((currentPrice - oldPrice) / oldPrice) * 100;
                }

                let fmv = null;
                let fmvDelta = null;
                if (team.current_odds !== null && team.current_odds !== undefined) {
                    const odds = team.current_odds;
                    let impliedProb;
                    if (odds > 0) {
                        impliedProb = 100 / (odds + 100);
                    } else {
                        impliedProb = (-odds) / (-odds + 100);
                    }
                    const betAmount = 1000 * impliedProb;
                    fmv = betAmount;
                    fmvDelta = team.current_price - fmv;
                }

                return {
                    ...team,
                    priceChangePercent,
                    fmv,
                    fmvDelta
                };
            });

            setPrices(enrichedExchange);
            if (selectedTeam) {
                const updated = enrichedExchange.find(t => t.team_symbol === selectedTeam);
                if (updated) setDetailData(updated);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to get model data:', error);
        }
    };

    useEffect(() => {
        fetchexchangedata();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY || window.pageYOffset;
            const screenHeight = window.innerHeight;
            setShowStickyBar(scrollY > screenHeight * 0.25);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Fetch open orders when user is logged in
    useEffect(() => {
        if (!user) return;
        // getUser();
        const fetchOrders = async () => {
            setOrdersLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/user/${user.id}/orders`);
                const result = await res.json();
                if (result.success) {
                    // Only show open/partial orders
                    const open = (result.data || []).filter(o =>
                        o.status === 'open' || o.status === 'partial'
                    );
                    setOrders(open);
                } else {
                    setOrders([]);
                }
            } catch (err) {
                console.error('Failed to fetch orders:', err);
                setOrders([]);
            } finally {
                setOrdersLoading(false);
            }
        };
        fetchOrders();
    }, [user]);

    // Two-phase ticker fetch: runs after prices loads
    useEffect(() => {
        if (prices.length === 0) return;
        const active = prices.filter(t => t.status !== 'inactive' && t.status !== 'eliminated');
        if (active.length === 0) return;

        const buildTicker = async () => {
            // Phase 1: fetch 24hr stats for all active securities in parallel
            const statsResults = await Promise.all(
                active.map(async (team) => {
                    try {
                        const res = await fetch(`${API_BASE_URL}/api/exchange/stats/24h/${encodeURIComponent(team.team_symbol)}`);
                        if (!res.ok) return null;
                        const s = await res.json();
                        const vol = s?.data?.volume ?? 0;
                        return { team, volume: typeof vol === 'number' ? vol : parseInt(vol) || 0 };
                    } catch {
                        return { team, volume: 0 };
                    }
                })
            );

            // Sort by volume desc, take top 10
            const top10 = statsResults
                .filter(Boolean)
                .sort((a, b) => b.volume - a.volume)
                .slice(0, 10);

            // Phase 2: fetch order books for top 10 in parallel
            const enriched = await Promise.all(
                top10.map(async ({ team, volume }) => {
                    try {
                        const res = await fetch(`${API_BASE_URL}/api/exchange/order-book/symbol/${encodeURIComponent(team.team_symbol)}`);
                        if (!res.ok) return { team, volume, bid: null, ask: null };
                        const raw = await res.json();
                        const book = raw.success ? raw.data : raw;
                        const bid = book?.bids?.[0]?.price ?? null;
                        const ask = book?.asks?.[0]?.price ?? null;
                        return { team, volume, bid, ask };
                    } catch {
                        return { team, volume, bid: null, ask: null };
                    }
                })
            );

            setTickerData(enriched);
        };

        buildTicker();
    }, [prices]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchStaticDetail = async () => {
        setDetailLoading(true);
        try {
            const [bookRes, tradesRes, statsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/exchange/order-book/symbol/${encodeURIComponent(selectedTeam)}`),
                fetch(`${API_BASE_URL}/api/exchange/trades/symbol/${encodeURIComponent(selectedTeam)}`),
                fetch(`${API_BASE_URL}/api/exchange/stats/24h/${encodeURIComponent(selectedTeam)}`)
            ]);
            if (bookRes.ok) {
                const b = await bookRes.json();
                if (b.success) setOrderBook(b.data);
            }
            if (tradesRes.ok) {
                const t = await tradesRes.json();
                if (t.success) setRecentTrades((t.data || []).slice(0, 10));
            }
            if (statsRes.ok) {
                const s = await statsRes.json();
                if (s.success) setStats24hr(s.data);
            }
        } catch (err) {
            console.error('Detail fetch error:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    // Fetch detail panel data when a team is selected
    useEffect(() => {
        if (!selectedTeam) return;

        let priceInterval = null;
        let bookInterval = null;

        fetchStaticDetail(); // Initial fetch immediately on team select

        // Set initial detailData from prices array immediately
        const initial = prices.find(p => p.team_symbol === selectedTeam);
        if (initial) {
            setDetailData(initial);
            // Do NOT setOrderPrice here
        }

        return () => {
            clearInterval(priceInterval);
            clearInterval(bookInterval);
        };
    }, [selectedTeam]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectedTeam) return;
        if (detailData && detailData.current_price != null) {
            setOrderPrice(orderBook.asks?.[0]?.price);
        }
        // eslint-disable-next-line
    }, [detailData, orderBook, selectedTeam]);

    useEffect(() => {
        if (!prices || prices.length === 0) return;

        const newHighlights = {};
        prices.forEach(team => {
            const prev = prevPricesRef.current?.find(t => t.team_symbol === team.team_symbol);
            if (prev && prev.current_price !== team.current_price) {
                newHighlights[team.team_symbol] = true;
                setTimeout(() => {
                    setHighlightedTeams(h => ({ ...h, [team.team_symbol]: false }));
                }, 1000);
            }
        });
        if (Object.keys(newHighlights).length > 0) {
            setHighlightedTeams(h => ({ ...h, ...newHighlights }));
        }
        prevPricesRef.current = prices;
    }, [prices]);
    
    const handleSelectTeam = (symbol) => {
        if (symbol === selectedTeam) return;
        setSelectedTeam(symbol);
        setDetailData(null);
        setOrderBook({ bids: [], asks: [] });
        setRecentTrades([]);
        setStats24hr(null);
        setOrderType('buy');
        setOrderMsg(null);
    };

    useEffect(() => {
        if (!selectedTeam) return;
        // When prices or selectedTeam changes, update detailData
        const found = prices.find(p => p.team_symbol === selectedTeam);
        if (found) setDetailData(found);
    }, [prices, selectedTeam]);

    // useEffect(() => {
    // if (user && user.id) {
    //     getUser();
    // }
    // }, [user]);

    const getUser = async () => {
            fetchUserProfile(user.id);
        };

    const handleSubmitOrder = async () => {
        if (isSubmitting) return;  // Prevent duplicate submissions
        if (!user) { setOrderMsg({ type: 'error', text: 'Please log in to trade.' }); return; }
        if (!orderQty) { setOrderMsg({ type: 'error', text: 'Enter quantity.' }); return; }
        setIsSubmitting(true);
        setOrderMsg(null);
        
        let executionPrice = orderPrice;
        if (orderAction === 'market') {
            if (orderType === 'buy') {
                executionPrice = orderBook.asks?.[0]?.price ?? null;
            } else if (orderType === 'sell') {
                executionPrice = orderBook.bids?.[0]?.price ?? null;
            }
            if (executionPrice === null) {
                setOrderMsg({ type: 'error', text: 'No liquidity available for Market order. Please select Limit order and try again.' });
                setIsSubmitting(false);
                return;
            }
        }

        try {
            // Minimum delay for UX
            const [response] = await Promise.all([
                fetch(`${API_BASE_URL}/api/exchange/order`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: user.id,
                        security_symbol: selectedTeam,
                        type: orderType,
                        price: Number(executionPrice),
                        quantity: Number(orderQty)
                    })
                }),
                new Promise(resolve => setTimeout(resolve, 500))
            ]);

            const result = await response.json();
            if (!result.success) {
                throw new Error(result.error || 'Failed to place order');
            }

            setOrderMsg({ type: 'success', text: 'Order placed successfully!' });
            // Optionally reset form fields:
            // setOrderPrice('');
            setOrderQty(1);

            // 🔁 Refresh detail panel and user data
            await Promise.all([
                fetchStaticDetail(),
                fetchexchangedata(),
                getUser()
            ]);
        } catch (err) {
            console.error(err);
            let cleanMessage = "There was an issue placing your order.";
            if (err.message.toLowerCase().includes("insufficient funds")) {
                cleanMessage = "You don't have enough funds to place this buy order.";
            } else if (err.message.toLowerCase().includes("insufficient shares")) {
                cleanMessage = "You don't own enough shares to place this sell order.";
            } else if (err.message.toLowerCase().includes("security not found")) {
                cleanMessage = "This team/security is no longer available.";
            }
            setOrderMsg({ type: 'error', text: cleanMessage });
        } finally {
            setIsSubmitting(false);
        }
    };

    const teamsBySport = prices.reduce((acc, team) => {
        const sport = team.sport || 'Unknown';
        if (!acc[sport]) acc[sport] = [];
        acc[sport].push(team);
        return acc;
    }, {});

    const sportFilterConfig = [
        { key: 'ALL', label: 'All Sports', icon: SportsScore },
        { key: 'NFL', label: 'NFL', icon: SportsFootball },
        { key: 'MLB', label: 'MLB', icon: SportsBaseball },
        { key: 'NBA', label: 'NBA', icon: SportsBasketball },
        { key: 'NHL', label: 'NHL', icon: SportsHockey },
        { key: 'NCAAF', label: 'NCAAF', icon: SportsFootball },
        { key: 'NCAAB', label: 'NCAAB', icon: SportsBasketball },
        { key: 'Soccer', label: 'Soccer', icon: Sports },
        { key: 'Golf-Masters', label: 'The Masters', icon: SportsGolf },
        { key: 'Golf-The-Open', label: 'The Open', icon: SportsGolf },
        { key: 'Golf-US-Open', label: 'US Open', icon: SportsGolf },
        { key: 'Golf-PGA', label: 'PGA Championship', icon: SportsGolf },
    ];

    const availableSports = ['ALL', ...Object.keys(teamsBySport)];
    const filteredSportConfig = sportFilterConfig.filter(config =>
        availableSports.includes(config.key)
    );

    const sportsData = Object.entries(teamsBySport).map(([sport, teams]) => ({
        sport,
        teams,
    })).sort((a, b) => a.sport.localeCompare(b.sport));

    const filteredSportsData = (selectedSport === 'ALL'
        ? sportsData
        : sportsData.filter(({ sport }) => sport === selectedSport)
    ).map(sportData => {
        const filteredTeams = sportData.teams.filter(team => {
            if (team.status === 'inactive') return false;
            const symbol = (team.team_symbol || '').toLowerCase();
            const teamName = (team.team || '').toLowerCase();
            const query = (searchQuery || '').toLowerCase();
            return symbol.includes(query) || teamName.includes(query);
        });
        if (filteredTeams.length === 0) return null;
        return { ...sportData, teams: filteredTeams };
    }).filter(Boolean);

    const topMovers = prices
        .filter(team => team.priceChangePercent !== 0)
        .sort((a, b) => Math.abs(b.priceChangePercent) - Math.abs(a.priceChangePercent))
        .slice(0, 10);

    const topFMVDeltas = prices
        .filter(team => team.fmvDelta !== null && team.fmvDelta !== undefined)
        .sort((a, b) => Math.abs(b.fmvDelta) - Math.abs(a.fmvDelta))
        .slice(0, 10);

    // Market summary stats
    const activeSecurities = prices.filter(t => t.status !== 'inactive' && t.status !== 'eliminated');
    const avgPrice = activeSecurities.length
        ? activeSecurities.reduce((sum, t) => sum + t.current_price, 0) / activeSecurities.length
        : 0;
    const gainers = activeSecurities.filter(t => t.priceChangePercent > 0).length;
    const losers = activeSecurities.filter(t => t.priceChangePercent < 0).length;
    const isDesktop = window.innerWidth > 768; 

    return (
        <>
            {/* Scrolling Ticker Tape — Top 10 by Volume */}
            <div className="ex-ticker-bar ex-ticker-tape-bar">
                {tickerData.length === 0 ? (
                    <div className="ex-ticker-loading">Loading market data...</div>
                ) : (
                    <div className="ex-ticker-track">
                        {/* Duplicate items for seamless loop */}
                        {[...tickerData, ...tickerData].map(({ team, volume, bid, ask }, idx) => (
                            <div
                                key={idx}
                                className="ex-ticker-card"
                                onClick={() => handleSelectTeam(team.team_symbol)}
                            >
                                <span className="ex-ticker-card-name">
                                    {team.team}
                                    {team.status && team.status !== 'active' && (
                                        <span className="ex-ticker-status" style={{ marginLeft: 6, color: '#d9534f', fontWeight: 600, fontSize: '0.9em' }}>
                                        ({team.status.charAt(0).toUpperCase() + team.status.slice(1)})
                                        </span>
                                    )}
                                    </span>
                                <span className="ex-ticker-card-price"><span className={highlightedTeams[team.team_symbol] ? "highlight" : ""} style={{ color: "#007bff" }}>
                                    ${team.current_price.toFixed(2)}
                                </span></span>
                                <span className={`ex-ticker-card-change ${team.priceChangePercent >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                    {team.priceChangePercent >= 0 ? '▲' : '▼'} {Math.abs(team.priceChangePercent).toFixed(1)}%
                                </span>
                                <span className="ex-ticker-card-meta">
                                    B {bid !== null ? `$${bid.toFixed(2)}` : '—'} · A {ask !== null ? `$${ask.toFixed(2)}` : '—'}
                                </span>
                                <span className="ex-ticker-card-vol">Vol {volume.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>


            {/* Main Layout */}
            <div className="ex-layout">

                {/* Left Sidebar */}
                <div className="ex-account-summary hide-on-mobile">
                <div className="ex-sidebar">
                    {user && userProfile ? (
                        <>
                            {/* ── Account Summary ── */}
                            <div className="ex-account-summary">
                                <div className="ex-account-row">
                                    <AccountBalance className="ex-account-icon" fontSize="small" />
                                    <span className="ex-account-label">Account Value</span>
                                </div>
                                <div className="ex-account-nav">
                                    ${(userProfile.exchange_nav || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="ex-account-cash">
                                    <span className="ex-account-sublabel">Cash Available</span>
                                    <span className="ex-account-cash-val">
                                        ${(userProfile.exchange_avail || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>

                            {/* ── Positions (Holdings) ── */}
                            <div className="ex-panel">
                                <div className="ex-panel-header">
                                    <span className="ex-panel-title">
                                        <TrendingUp style={{ fontSize: 14, marginRight: 5, verticalAlign: 'middle' }} />
                                        Positions
                                    </span>
                                    <span className="ex-panel-count">
                                        {(userProfile.portfolio || []).filter(h => h.quantity > 0).length}
                                    </span>
                                </div>

                                {(userProfile.portfolio || []).filter(h => h.quantity > 0).length === 0 ? (
                                    <div className="ex-panel-empty">No open positions</div>
                                ) : (
                                    <>
                                        <div className="ex-panel-cols">
                                            <span>Symbol</span>
                                            <span>Last · P&amp;L</span>
                                        </div>
                                        <div className="ex-panel-rows">
                                            {(userProfile.portfolio || [])
                                                .filter(h => h.quantity > 0)
                                                .map((holding, idx) => {
                                                    const sym = holding.symbol || holding.security_id || '';
                                                    const mktTeam = prices.find(p => p.team_symbol === sym);
                                                    const currentPrice = mktTeam ? mktTeam.current_price : null;
                                                    const avgCost = holding.avg_price || 0;
                                                    const qty = holding.quantity || 0;
                                                    const unrealizedPnL = currentPrice !== null
                                                        ? (currentPrice - avgCost) * qty
                                                        : null;
                                                    const pnlPct = avgCost > 0 && currentPrice !== null
                                                        ? ((currentPrice - avgCost) / avgCost) * 100
                                                        : null;

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="ex-row"
                                                            onClick={() => handleSelectTeam(sym)}
                                                        >
                                                            <div className="ex-row-left">
                                                                <span className="ex-symbol">{mktTeam?.team || sym}</span>
                                                                <span className="ex-row-name">
                                                                    {sym} · {qty} · avg ${avgCost.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            <div className="ex-row-right">
                                                                <span className="ex-row-price">
                                                                    {currentPrice !== null ? `$${currentPrice.toFixed(2)}` : '—'}
                                                                </span>
                                                                {unrealizedPnL !== null && (
                                                                    <span className={`ex-row-pnl ${unrealizedPnL >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                                                        {unrealizedPnL >= 0 ? '+' : ''}${unrealizedPnL.toFixed(0)}
                                                                        {pnlPct !== null && ` (${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%)`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* ── Open Orders ── */}
                            <div className="ex-panel">
                                <div className="ex-panel-header">
                                    <span className="ex-panel-title">
                                        <Receipt style={{ fontSize: 14, marginRight: 5, verticalAlign: 'middle' }} />
                                        Open Orders
                                    </span>
                                    <span className="ex-panel-count">{orders.length}</span>
                                </div>

                                {ordersLoading ? (
                                    <div className="ex-panel-empty">Loading...</div>
                                ) : orders.length === 0 ? (
                                    <div className="ex-panel-empty">No open orders</div>
                                ) : (
                                    <div className="ex-panel-rows">
                                        {orders.map((order, idx) => {
                                            const sym = order.symbol || order.security_id || '';
                                            const isBuy = (order.type || order.order_type || '').toLowerCase() === 'buy';
                                            const statusLabel = order.status === 'partial'
                                                ? `Partial · ${order.filled_quantity || 0}/${order.quantity} filled`
                                                : 'Open';

                                            return (
                                                <div
                                                    key={idx}
                                                    className="ex-order-row"
                                                    onClick={() => handleSelectTeam(sym)}
                                                >
                                                    <div className="ex-order-top">
                                                        <span className="ex-row-symbol">{sym}</span>
                                                        <span className={`ex-order-status ${isBuy ? 'ex-order-buy' : 'ex-order-sell'}`}>
                                                            {statusLabel}
                                                        </span>
                                                    </div>
                                                    <div className="ex-order-detail">
                                                        <span className={`ex-order-type ${isBuy ? 'ex-positive' : 'ex-negative'}`}>
                                                            {isBuy ? 'Buy' : 'Sell'} {order.quantity} sh
                                                        </span>
                                                        <span className="ex-order-price">
                                                            Limit ${parseFloat(order.price || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Logged-out fallback: Top Movers + Top Value */}
                            <div className="ex-account-summary hide-on-mobile">
                            <div className="ex-panel">
                                <div className="ex-panel-header">
                                    <span className="ex-panel-title">🔥 Top Movers</span>
                                    <FilterList className="ex-panel-icon" fontSize="small" />
                                </div>
                                <div className="ex-panel-cols">
                                    <span>Symbol</span>
                                    <span>Change</span>
                                </div>
                                <div className="ex-panel-rows">
                                    {topMovers.map(team => (
                                        <div
                                            key={team._id}
                                            className="ex-row"
                                            onClick={() => history.push(`/exchange/${encodeURIComponent(team.team_symbol)}`)}
                                        >
                                            <div className="ex-row-left">
                                                <span className="ex-row-symbol">{team.team}</span>
                                                <span className="ex-row-name">{team.team_symbol}</span>
                                            </div>
                                            <span className={`ex-row-value ${team.priceChangePercent >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                                {team.priceChangePercent >= 0 ? '+' : ''}{team.priceChangePercent.toFixed(1)}%
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="ex-panel" style={{ marginTop: '16px' }}>
                                <div className="ex-panel-header">
                                    <span className="ex-panel-title">⚖️ Top Value</span>
                                    <FilterList className="ex-panel-icon" fontSize="small" />
                                </div>
                                <div className="ex-panel-cols">
                                    <span>Symbol</span>
                                    <span>FMV Delta</span>
                                </div>
                                <div className="ex-panel-rows">
                                    {topFMVDeltas.map(team => (
                                        <div
                                            key={team._id}
                                            className="ex-row"
                                            onClick={() => history.push(`/exchange/${encodeURIComponent(team.team_symbol)}`)}
                                        >
                                            <div className="ex-row-left">
                                                <span className="ex-row-symbol">{team.team}</span>
                                                <span className="ex-row-name">{team.team_symbol}</span>
                                            </div>
                                            <span className={`ex-row-value ${team.fmvDelta >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                                {team.fmvDelta >= 0 ? '+' : '-'}${Math.abs(team.fmvDelta).toFixed(0)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            </div>
                        </>
                    )}
                </div>
                </div>

                {/* Main Content — splits into list + detail */}
                <div className="ex-main">

                    {/* Help Button */}
                    <div
                        className="floating-help-btn"
                        onClick={() => { setShowHowModal(true); setHowPage(0); }}
                        title="How does this work?"
                    >
                        ?
                    </div>

                    {/* How It Works Modal */}
                    {showHowModal && (
                        <div className="modal-overlay" onClick={() => setShowHowModal(false)}>
                            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: 320, maxWidth: 420 }}>
                                <button className="modal-close" onClick={() => setShowHowModal(false)}>×</button>
                                <div style={{ minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <div style={{ marginBottom: 24, fontSize: '1.1em', textAlign: 'center' }}>
                                        <div dangerouslySetInnerHTML={{ __html: howPages[howPage] }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                                        <button onClick={() => setHowPage(p => Math.max(0, p - 1))} disabled={howPage === 0}
                                            style={{ fontSize: '2em', background: 'none', border: 'none', color: howPage === 0 ? '#ccc' : '#007bff', cursor: howPage === 0 ? 'default' : 'pointer' }}
                                            aria-label="Previous">&#8592;</button>
                                        <span style={{ fontSize: '1em' }}>{howPage + 1} / {howPages.length}</span>
                                        {howPage === howPages.length - 1 ? (
                                            <button className="get-started-btn" onClick={() => setShowHowModal(false)}
                                                style={{ background: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontWeight: 600, fontSize: '1.1em', cursor: 'pointer' }}>
                                                Get Started
                                            </button>
                                        ) : (
                                            <button onClick={() => setHowPage(p => Math.min(howPages.length - 1, p + 1))}
                                                style={{ fontSize: '2em', background: 'none', border: 'none', color: '#007bff', cursor: 'pointer' }}
                                                aria-label="Next">&#8594;</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Top bar: Search + Sport Filter */}
                    <div className="ex-top-bar">
                        <div className="ex-search-bar">
                            <SearchIcon className="ex-search-icon" />
                            <input
                                type="text"
                                className="ex-search-input"
                                placeholder="Search by symbol or team name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="sport-filter-bar">
                            {filteredSportConfig.map(({ key, label, icon: IconComponent }) => (
                                <button
                                    key={key}
                                    className={`sport-filter-btn ${selectedSport === key ? 'active' : ''}`}
                                    onClick={() => setSelectedSport(key)}
                                >
                                    <IconComponent className="sport-icon" />
                                    {label}
                                    {key !== 'ALL' && teamsBySport[key] && (
                                        <span className="team-count-badge badge bg-secondary">
                                            {teamsBySport[key].length}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Split view: Securities List OR Detail Panel */}
                    <div className="ex-split">

                        {/* ── Securities List — only shown when nothing selected ── */}
                        {!selectedTeam && (
                            <div className="ex-list ex-list-full">
                                {loading ? (
                                    <div className="text-center my-5">
                                        <div className="spinner-border text-primary" role="status" />
                                    </div>
                                ) : (
                                    filteredSportsData.map(({ sport, teams }) => (
                                        <div key={sport}>
                                            <div className="ex-list-group-header">
                                                <span>{sport}</span>
                                                <span className="ex-section-count">{teams.length}</span>
                                            </div>
                                            {/* Full-width table header */}
                                            <div className="ex-table-header ex-list-table-header">
                                                <span className="ex-col-symbol">Symbol</span>
                                                <span className="ex-col-last">Last</span>
                                                <span className="ex-col-change">Change</span>
                                                <span className="ex-col-fmv">FMV</span>
                                                <span className="ex-col-delta">FMV Delta</span>
                                            </div>
                                            {teams.map((team) => {
                                                const isEliminated = team.status === 'eliminated';
                                                const isChamp = team.current_price === 1000;
                                                return (
                                                    <div
                                                        key={team._id}
                                                        className={`ex-table-row ex-list-table-row ${isEliminated ? 'ex-row-eliminated' : ''}`}
                                                        onClick={() => handleSelectTeam(team.team_symbol)}
                                                    >
                                                        <div className="ex-col-symbol">
                                                            <span className="ex-symbol">{team.team}</span>
                                                            <span className="ex-team-name">{team.team_symbol}</span>
                                                        </div>
                                                        <span className="ex-col-last ex-price">
                                                            ${team.current_price.toFixed(2)}
                                                            {isChamp && <span className="ex-champ-badge">👑</span>}
                                                        </span>
                                                        <span className={`ex-col-change ${team.priceChangePercent >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                                            {team.priceChangePercent !== 0
                                                                ? `${team.priceChangePercent >= 0 ? '+' : ''}${team.priceChangePercent.toFixed(2)}%`
                                                                : '—'}
                                                        </span>
                                                        <span className="ex-col-fmv">
                                                            {team.fmv != null ? `$${team.fmv.toFixed(2)}` : '—'}
                                                        </span>
                                                        <span className={`ex-col-delta ${team.fmvDelta >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                                            {team.fmvDelta != null
                                                                ? `${team.fmvDelta >= 0 ? '+' : ''}$${team.fmvDelta.toFixed(2)}`
                                                                : '—'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {showStickyBar && selectedTeam && (
                            <div className="mini-sticky-bar">
                                <span style={{ fontWeight: 700 }}>{detailData?.team}</span>
                                <span style={{ color: "var(--accent-gold)", fontSize: "0.8rem", marginLeft: 12 }}>{detailData?.team_symbol}</span>
                                {detailData?.current_price && (
                                    <span className={highlightedTeams[detailData.team_symbol] ? "highlight" : ""} style={{ marginLeft: 12, color: "#007bff" }}>
                                        ${detailData.current_price.toFixed(2)}
                                    </span>
                                )}
                            </div>
                        )}
                        {/* ── Security Detail Panel — only shown when a team is selected ── */}
                        {selectedTeam && (
                        <div className="ex-detail ex-detail-full">

                            {/* Breadcrumb / back bar */}
                            <div className="ex-breadcrumb">
                                <button className="ex-breadcrumb-back" onClick={() => setSelectedTeam(null)}>
                                    ← Back
                                </button>
                                <span className="ex-breadcrumb-divider">/</span>
                                {detailData?.sport && (
                                    <>
                                        <span
                                            className="ex-breadcrumb-sport"
                                            onClick={() => { setSelectedSport(detailData.sport); setSelectedTeam(null); }}
                                        >
                                            {detailData.sport}
                                        </span>
                                        <span className="ex-breadcrumb-divider">/</span>
                                    </>
                                )}
                                <span className="ex-breadcrumb-symbol">{selectedTeam}</span>
                                {detailData?.team && (
                                    <span className="ex-breadcrumb-name">{detailData.team}</span>
                                )}
                            </div>

                            {detailLoading && !detailData ? (
                                <div className="ex-detail-empty">
                                    <div className="spinner-border text-primary" role="status" />
                                </div>
                            ) : detailData ? (
                                <>
                                    {/* ── Fidelity-style Header Bar ── */}
                                    <div className="ex-detail-header">
                                        <div className="ex-detail-symbol-block">
                                            <span className="ex-detail-symbol">{detailData.team}</span>
                                            <span className="ex-detail-name">{detailData.team_symbol}</span>
                                            {detailData.status && detailData.status !== 'active' && (
                                                <span className="ex-detail-status" style={{ marginLeft: 6, color: '#d9534f', fontWeight: 600, fontSize: '0.9em' }}>
                                                    ({detailData.status.toUpperCase()})
                                                </span>
                                            )}
                                        </div>

                                        {/* ── Hero value cards ── */}
                                        <div className="ex-hero-grid">
                                            <div className="ex-hero-card">
                                                <span className="ex-hero-label">COST PER SHARE</span>
                                                <span className={`ex-hero-value ${highlightedTeams[detailData.team_symbol] ? "highlight" : ""}`}>
                                                    ${detailData.current_price.toFixed(2)}
                                                    {detailData.current_price === 1000 && <span style={{ marginLeft: 6, fontSize: '0.7em', color: '#FFD700' }}>👑</span>}
                                                </span>
                                                <span className="ex-hero-sub">Current market price</span>
                                            </div>
                                            <div className={`ex-hero-card ${detailData.fmv != null ? (detailData.fmv > detailData.current_price ? 'ex-hero-card-under' : 'ex-hero-card-over') : ''}`}>
                                                <span className="ex-hero-label">FAIR MARKET VALUE</span>
                                                <span className="ex-hero-value">
                                                    {detailData.fmv != null ? `$${detailData.fmv.toFixed(2)}` : '—'}
                                                </span>
                                                <span className="ex-hero-sub">
                                                    {detailData.current_odds != null
                                                        ? `Live Odds: ${detailData.current_odds > 0 ? '+' : ''}${detailData.current_odds}`
                                                        : 'No live odds available'}
                                                </span>
                                            </div>
                                            {/* </div>
                                            <div className="ex-hero-grid"> */}
                                            <div className="ex-hero-card ex-hero-card-fixed">
                                                <span className="ex-hero-label">PRICE HISTORY</span>
                                                <div style={{ width: "50vw", height: 100 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={detailData?.price_history?.slice(-20) || []}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="date" hide />
                                                            <YAxis domain={['auto', 'auto']} hide />
                                                            <Tooltip
                                                                contentStyle={{ fontSize: 12 }}
                                                                formatter={(value) => `$${parseFloat(value).toFixed(2)}`}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="price"
                                                                stroke="#007bff"
                                                                strokeWidth={2}
                                                                dot={false}
                                                                isAnimationActive={false}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                {/* <span className="ex-hero-sub">Last 20 prices</span> */}
                                            </div>
                                        </div>

                                        {/* Payout description */}
                                        <div className="ex-payout-desc">
                                            1 share of <strong>{detailData.team}</strong> pays <strong>$1,000</strong> if they win
                                        </div>

                                        <div className="ex-detail-meta-grid">
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">Bid</span>
                                                <span className="ex-detail-meta-val ex-positive">
                                                    {orderBook.bids && orderBook.bids.length > 0
                                                        ? `$${orderBook.bids[0].price.toFixed(2)}`
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">Ask</span>
                                                <span className="ex-detail-meta-val ex-negative">
                                                    {orderBook.asks && orderBook.asks.length > 0
                                                        ? `$${orderBook.asks[0].price.toFixed(2)}`
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">24h High</span>
                                                <span className="ex-detail-meta-val">
                                                    {stats24hr?.high != null ? `$${parseFloat(stats24hr.high).toFixed(2)}` : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">24h Low</span>
                                                <span className="ex-detail-meta-val">
                                                    {stats24hr?.low != null ? `$${parseFloat(stats24hr.low).toFixed(2)}` : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">Volume</span>
                                                <span className="ex-detail-meta-val">
                                                    {stats24hr?.volume != null ? stats24hr.volume : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">FMV</span>
                                                <span className="ex-detail-meta-val">
                                                    {detailData.fmv != null ? `$${detailData.fmv.toFixed(2)}` : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">FMV Delta</span>
                                                <span className={`ex-detail-meta-val ${detailData.fmvDelta >= 0 ? 'ex-positive' : 'ex-negative'}`}>
                                                    {detailData.fmvDelta != null
                                                        ? `${detailData.fmvDelta >= 0 ? '+' : ''}$${detailData.fmvDelta.toFixed(2)}`
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div className="ex-detail-meta-item">
                                                <span className="ex-detail-meta-label">Odds</span>
                                                <span className="ex-detail-meta-val">
                                                    {detailData.current_odds != null
                                                        ? `${detailData.current_odds > 0 ? '+' : ''}${detailData.current_odds}`
                                                        : '—'}
                                                </span>
                                            </div>
                                        </div>
                                            <button
                                                className="ex-action-btn ex-chart-btn"
                                                onClick={() => { window.scrollTo(0, 0); history.push(`/exchange/${encodeURIComponent(selectedTeam)}`); }}
                                                title="View full chart"
                                            >Detail ↗</button>

                                    </div>

                                    {/* ── Order Form ── */}
                                    <div className="ex-order-form">
                                        {/* Buy / Sell action buttons */}
                                        <div className="ex-detail-actions">
                                            <button
                                                className={`ex-action-btn ex-buy-btn ${orderType === 'buy' ? 'active' : ''}`}
                                                onClick={() => {
                                                    setOrderType('buy');
                                                    const bestAsk = orderBook.asks?.[0]?.price ?? null;
                                                    if (bestAsk !== null) setOrderPrice(bestAsk.toFixed(2));
                                                }}
                                            >Buy</button>
                                            <button
                                                className={`ex-action-btn ex-sell-btn ${orderType === 'sell' ? 'active' : ''}`}
                                                onClick={() => {
                                                    setOrderType('sell');
                                                    const bestBid = orderBook.bids?.[0]?.price ?? null;
                                                    if (bestBid !== null) setOrderPrice(bestBid.toFixed(2));
                                                }}
                                            >Sell</button>
                                        </div>
                                        {/* Limit/Market selector */}
                                        <div className="ex-order-type-row" style={{ display: 'flex', gap: '24px', margin: '12px 0' }}>
                                            <label style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="radio"
                                                    name="orderAction"
                                                    value="market"
                                                    checked={orderAction === 'market'}
                                                    onChange={() => {
                                                        setOrderAction('market');
                                                        if (orderType === 'buy') {
                                                            const bestAsk = orderBook.asks?.[0]?.price ?? null;
                                                            if (bestAsk !== null) setOrderPrice(bestAsk.toFixed(2));
                                                        } else if (orderType === 'sell') {
                                                            const bestBid = orderBook.bids?.[0]?.price ?? null;
                                                            if (bestBid !== null) setOrderPrice(bestBid.toFixed(2));
                                                        }
                                                    }}
                                                />
                                                Market
                                            </label>
                                            <label style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="radio"
                                                    name="orderAction"
                                                    value="limit"
                                                    checked={orderAction === 'limit'}
                                                    onChange={() => {
                                                        setOrderAction('limit');
                                                        setOrderPrice(detailData.current_price.toFixed(2));
                                                    }}
                                                />
                                                Limit
                                            </label>
                                        </div>
                                        <div className="ex-order-form-row">
                                            <div className="ex-form-group">
                                                <label className="ex-form-label">Price ($)</label>
                                                <div className="ex-form-price-row">
                                                    <input
                                                        type="number"
                                                        className="ex-form-input"
                                                        value={orderPrice}
                                                        onChange={e => setOrderPrice(e.target.value)}
                                                        min="0" max="1000" step="0.01"
                                                        disabled={orderAction === 'market'}
                                                        style={orderAction === 'market' ? { background: '#eee', color: '#6f6e6e', cursor: 'not-allowed' } : {}}
                                                    />
                                                    {orderAction === 'limit' && (
                                                        <div className="ex-price-quick">
                                                            <button onClick={() => setOrderPrice((parseFloat(orderPrice || 0) * 0.99).toFixed(2))}>-1%</button>
                                                            <button onClick={() => detailData.fmv && setOrderPrice(detailData.fmv.toFixed(2))}>FMV</button>
                                                            <button onClick={() => setOrderPrice((parseFloat(orderPrice || 0) * 1.01).toFixed(2))}>+1%</button>
                                                        </div>
                                                    )}
                                                </div>
                                                    {orderAction === 'market' ? (
                                                        <span style={{ marginLeft: '8px', fontSize: '0.8em', color: '#888' }}>
                                                            Market orders execute at the best available price and do not guarantee a specific price.
                                                        </span>
                                                    ) : null}
                                            </div>
                                            <div className="ex-form-group">
                                                <label className="ex-form-label">Quantity</label>
                                                <div className="ex-form-price-row">
                                                    <input
                                                        type="number"
                                                        className="ex-form-input"
                                                        value={orderQty}
                                                        onChange={e => setOrderQty((e.target.value))}
                                                        min="1"
                                                    />
                                                    <div className="ex-price-quick">
                                                        <button onClick={() => setOrderQty(q => q + 1)}>+1</button>
                                                        <button onClick={() => setOrderQty(q => q + 5)}>+5</button>
                                                        <button onClick={() => setOrderQty(q => q + 10)}>+10</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ex-order-total">
                                            Total: <strong>${(parseFloat(orderPrice || 0) * orderQty).toFixed(2)}</strong>
                                        </div>
                                        {orderMsg && (
                                            <div className={`ex-order-msg ${orderMsg.type === 'success' ? 'ex-order-msg-ok' : 'ex-order-msg-err'}`}>
                                                {orderMsg.text}
                                            </div>
                                        )}
                                        <button
                                            className={`ex-submit-btn ${orderType === 'buy' ? 'ex-submit-buy' : 'ex-submit-sell'}`}
                                            onClick={handleSubmitOrder}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Submitting...' : `Place ${orderType === 'buy' ? 'Buy' : 'Sell'} Order`}
                                            {isSubmitting && (
                                                <div className="modal-overlay">
                                                    <div className="modal-content" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                                                        <h3>Submitting Trade...</h3>
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden"></span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    </div>

                                    {/* ── Order Book + Recent Trades ── */}
                                    <div className="ex-book-trades">

                                        {/* Order Book */}
                                        <div className="ex-book">
                                            <div className="ex-book-header">Order Book</div>
                                            <div className="ex-book-cols-row">
                                                <div className="ex-book-side">
                                                    <div className="ex-book-header">Bids</div>
                                                    <div className="ex-book-col-header">
                                                        <span>Price</span><span>Qty</span>
                                                    </div>
                                                    {(orderBook.bids || []).slice(0, 8).map((bid, i) => (
                                                        <div key={i} className="ex-book-row ex-bid-row"
                                                            onClick={() => setOrderPrice(bid.price.toFixed(2))}>
                                                            <span className="ex-positive">${bid.price.toFixed(2)}</span>
                                                            <span>{bid.quantity - (bid.filled_quantity || 0)}</span>
                                                        </div>
                                                    ))}
                                                    {(orderBook.bids || []).length === 0 && (
                                                        <div className="ex-book-empty">No bids</div>
                                                    )}
                                                </div>
                                                <div className="ex-book-divider" />
                                                <div className="ex-book-side">
                                                    <div className="ex-book-header">Asks</div>
                                                    <div className="ex-book-col-header">
                                                        <span>Price</span><span>Qty</span>
                                                    </div>
                                                    {(orderBook.asks || []).slice(0, 8).map((ask, i) => {
                                                        const isIPO = ask.user_id && ask.user_id.endsWith('b589');
                                                        return (
                                                            <div key={i} className="ex-book-row ex-ask-row"
                                                                onClick={() => setOrderPrice(ask.price.toFixed(2))}>
                                                                <span className="ex-negative">${ask.price.toFixed(2)}</span>
                                                                <span>{ask.quantity - (ask.filled_quantity || 0)}{isIPO ? ' IPO' : ''}</span>
                                                            </div>
                                                        );
                                                    })}
                                                    {(orderBook.asks || []).length === 0 && (
                                                        <div className="ex-book-empty">No asks</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Recent Trades */}
                                        <div className="ex-trades">
                                            <div className="ex-book-header">Recent Trades</div>
                                            <div className="ex-book-col-header" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                                                <span>Time</span><span>Price</span><span>Qty</span>
                                            </div>
                                            {recentTrades.length === 0 ? (
                                                <div className="ex-book-empty">No recent trades</div>
                                            ) : (
                                                recentTrades.map((trade, i) => (
                                                    <div key={i} className="ex-trade-row">
                                                        <span>{trade.timestamp ? new Date(trade.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                                                        <span className="ex-positive">${parseFloat(trade.price).toFixed(2)}</span>
                                                        <span>{trade.quantity}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                    </div>
                                </>
                            ) : null}
                        </div>
                        )}{/* end selectedTeam && */}

                    </div>{/* end ex-split */}
                </div>
            </div>
        </>
    );
}

export default Exchange;
